import firebase from 'firebase/app';
// Add the Firebase products that you want to use
import "firebase/auth";
import "firebase/firestore";

class FirebaseAuthBackend {
    constructor(firebaseConfig) {
        if (firebaseConfig) {
            // Initialize Firebase
            firebase.initializeApp(firebaseConfig);
            firebase.auth().onAuthStateChanged((user) => {
                if (user) {
                    sessionStorage.setItem("authUser", JSON.stringify(user));
                } else {
                    sessionStorage.removeItem('authUser');
                }
            });
        }
    }

    /**
     * Resuelve cuando Firebase Auth terminó de restaurar (o no) la sesión.
     *
     * Al recargar la página, el guard de rutas corre antes de que
     * `firebase.auth().currentUser` esté poblado; leerlo en ese instante da
     * null y expulsaba al usuario aunque su sesión fuera válida (por ejemplo,
     * tras guardar una sesión, que recargaba la página). Esperar al primer
     * `onAuthStateChanged` evita esa carrera.
     */
    esperarAuthListo = () => {
        if (this._authListo) return this._authListo;
        this._authListo = new Promise((resolve) => {
            const cancelar = firebase.auth().onAuthStateChanged((user) => {
                cancelar();
                resolve(user);
            });
        });
        return this._authListo;
    }

    /**
     * Registers the user with given details
     */
    registerUser = (email, password) => {
        return new Promise((resolve, reject) => {
            firebase.auth().createUserWithEmailAndPassword(email, password).then((user) => {
                // eslint-disable-next-line no-redeclare
                var user = firebase.auth().currentUser;
                resolve(user);
            }, (error) => {
                reject(this._handleError(error));
            });
        });
    }

    /**
     * Login user with given details
     */
    // Se propaga el error tal cual, no sólo su `message`: quien llama necesita
    // el `code` (`auth/wrong-password`, `auth/user-not-found`…) para dar un
    // mensaje en español. Descartándolo, la interfaz sólo podía enseñar el
    // texto en inglés de Firebase o un aviso genérico.
    loginUser = (email, password) => {
        return firebase.auth().signInWithEmailAndPassword(email, password)
            .then(() => {
                const usuario = firebase.auth().currentUser;
                if (usuario) this.setLoggeedInUser(usuario);
                return usuario;
            });
    }

    /**
     * Canjea con Firebase el token de identidad que entrega Google Identity
     * Services (ver `services/googleIdentity.js`).
     *
     * Hace falta porque quien se registró con Google en la app móvil no tiene
     * contraseña: sin esto no puede entrar por la web de ninguna forma.
     */
    loginWithGoogleCredential = (idToken) => {
        const credencial = firebase.auth.GoogleAuthProvider.credential(idToken);
        return firebase.auth().signInWithCredential(credencial)
            .then(() => {
                const usuario = firebase.auth().currentUser;
                // `validate()` —que corre en el guard de cada ruta protegida—
                // lee `sessionStorage`, y esa clave sólo la escribe el callback
                // asíncrono `onAuthStateChanged`. Si navegamos antes de que ese
                // callback corra, el guard cree que no hay sesión y rebota al
                // login. Persistirla aquí elimina esa carrera.
                if (usuario) this.setLoggeedInUser(usuario);
                return usuario;
            });
    }

    /**
     * forget Password user with given details
     */
    forgetPassword = (email) => {
        return new Promise((resolve, reject) => {
            firebase.auth().sendPasswordResetEmail(email, { url: window.location.protocol + "//" + window.location.host + "/login" }).then(() => {
                resolve(true);
            }).catch((error) => {
                reject(this._handleError(error));
            })
        });
    }

    /**
     * Métodos con los que se puede entrar a una cuenta: `["password"]`,
     * `["google.com"]`, ambos, o vacío si no existe.
     *
     * Hace falta para no mentirle al usuario. Quien se registró con Google en
     * la app no tiene contraseña, y al intentar entrar con una Firebase
     * responde lo mismo que si la cuenta no existiera. Sin esta consulta el
     * portal le dice "no encontramos tu cuenta" a alguien que sí la tiene.
     */
    metodosDeAcceso = (email) => {
        return firebase.auth().fetchSignInMethodsForEmail(email)
            .catch((error) => {
                console.warn('[auth] no se pudieron consultar los métodos de acceso:', error);
                return [];
            });
    }

    /**
     * Logout the user
     */
    logout = () => {
        return new Promise((resolve, reject) => {
            firebase.auth().signOut().then(() => {
                resolve(true);
            }).catch((error) => {
                reject(this._handleError(error));
            })
        });
    }

    setLoggeedInUser = (user) => {
        sessionStorage.setItem("authUser", JSON.stringify(user));
    }

    /**
     * Returns the authenticated user
     *
     * La fuente de verdad es el SDK; `sessionStorage` es sólo una copia que
     * escribe `onAuthStateChanged`, y ese callback es asíncrono. Consultar
     * únicamente la copia provocaba que, justo después de iniciar sesión, el
     * guard de rutas creyera que no había sesión y devolviera al login —el
     * síntoma clásico de "entro y me rebota".
     *
     * El respaldo en `sessionStorage` sigue haciendo falta para el caso
     * contrario: al recargar la página, el SDK tarda un instante en restaurar
     * la sesión y `currentUser` todavía es null.
     */
    getAuthenticatedUser = () => {
        const usuarioActual = firebase.auth().currentUser;
        if (usuarioActual) return usuarioActual;

        const enSesion = sessionStorage.getItem('authUser');
        return enSesion ? JSON.parse(enSesion) : null;
    }

    /**
     * Handle the error
     * @param {*} error 
     */
    _handleError(error) {
        var errorMessage = error.message;
        return errorMessage;
    }
}

let _fireBaseBackend = null;

/**
 * Initilize the backend
 * @param {*} config 
 */
const initFirebaseBackend = (config) => {
    if (!_fireBaseBackend) {
        _fireBaseBackend = new FirebaseAuthBackend(config);
    }
    return _fireBaseBackend;
}

/**
 * Returns the firebase backend
 */
const getFirebaseBackend = () => {
    return _fireBaseBackend;
}

export { initFirebaseBackend, getFirebaseBackend };